import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, clearCache } from '../lib/supabase';
import { signIn as authSignIn } from '../lib/auth';

interface Profile {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  idecide_score: number;
  has_partner: boolean;
  partner_document_access: string | null;
  children_age_groups: string[] | null;
  dependents: string[] | null;
  onboarding_completed: boolean;
  payment_status: string;
  payment_intent_id: string | null;
  payment_completed: boolean;
  payment_date: string | null;
  subscription_status: string;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  error: string | null;
  profileError: string | null;
  retryProfileFetch: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  moduleLoadingStates: Record<string, boolean>;
  setModuleLoadingState: (module: string, loading: boolean) => void;
  clearModuleLoadingState: (module: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [moduleLoadingStates, setModuleLoadingStates] = useState<Record<string, boolean>>({});
  const initializedRef = useRef(false);
  // Refs that always hold the latest state values so async callbacks avoid stale closures
  const userRef = useRef<User | null>(null);
  const profileRef = useRef<Profile | null>(null);

  // Keep refs in sync so async callbacks (onAuthStateChange, visibilitychange) always
  // read the current value without stale closures.
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { profileRef.current = profile; }, [profile]);

  const fetchProfile = useCallback(async (userId: string): Promise<Profile | null> => {
    try {
      const { data: profileData, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (error) {
        if (error.code === 'PGRST116') {
          try {
            const { data: { user: currentUser } } = await supabase.auth.getUser();
            if (currentUser && currentUser.id === userId) {
              const { error: createError } = await supabase
                .from('profiles')
                .insert({
                  id: userId,
                  email: currentUser.email || '',
                  full_name: currentUser.user_metadata?.full_name || '',
                  avatar_url: currentUser.user_metadata?.avatar_url || '',
                  idecide_score: 0,
                  has_partner: currentUser.user_metadata?.has_partner || false,
                  partner_document_access: currentUser.user_metadata?.partner_document_access || '',
                  children_age_groups: currentUser.user_metadata?.children_age_groups || [],
                  dependents: currentUser.user_metadata?.dependents || [],
                  onboarding_completed: currentUser.user_metadata?.onboarding_completed || false,
                  payment_status: 'free',
                  payment_intent_id: null,
                  payment_completed: true,
                  payment_date: null,
                  subscription_status: 'free'
                });
              
              if (createError) {
                console.error('Failed to create profile:', createError);
                // Unique violation (23505) means another request already created it — just re-fetch
                if (createError.code === '23505') {
                  const { data: raceProfile } = await supabase
                    .from('profiles')
                    .select('*')
                    .eq('id', userId)
                    .single();
                  return raceProfile ?? null;
                }
                return null;
              }

              const { data: newProfile, error: fetchError } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', userId)
                .single();

              if (fetchError) return null;
              return newProfile;
            }
          } catch (createErr) {
            console.error('Profile creation failed:', createErr);
            return null;
          }
        }
        
        return null;
      }
      
      return profileData;
    } catch (err) {
      console.error('Profile fetch failed:', err);
      return null;
    }
  }, []);

  const retryProfileFetch = useCallback(async () => {
    if (!user) return;
    
    setProfileError(null);
    setLoading(true);
    
    const profileData = await fetchProfile(user.id);
    setProfile(profileData);
    setProfileError(null);
    setLoading(false);
  }, [user, fetchProfile]);

  useEffect(() => {
    let cancelled = false;
    
    const init = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (cancelled) return;
        
        if (session?.user) {
          setUser(session.user);
          setError(null);

          // Retry profile fetch with backoff — handles DB triggers that fire asynchronously
          let profileData = await fetchProfile(session.user.id);
          if (!profileData && !cancelled) {
            await new Promise(r => setTimeout(r, 1500));
            if (!cancelled) profileData = await fetchProfile(session.user.id);
          }
          if (!profileData && !cancelled) {
            await new Promise(r => setTimeout(r, 3000));
            if (!cancelled) profileData = await fetchProfile(session.user.id);
          }

          if (cancelled) return;
          setProfile(profileData);
          setProfileError(
            profileData
              ? null
              : 'Your profile could not be loaded. Please refresh the page or sign out and back in.'
          );
        } else {
          setUser(null);
          setProfile(null);
          setProfileError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setUser(null);
        setProfile(null);
        setError(err instanceof Error ? err.message : 'Authentication failed');
      } finally {
        if (!cancelled) {
          setLoading(false);
          initializedRef.current = true;
        }
      }
    };

    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!initializedRef.current) return;
        
        if (event === 'SIGNED_OUT') {
          setUser(null);
          setProfile(null);
          setError(null);
          setProfileError(null);
          return;
        }

        // TOKEN_REFRESHED fires every ~55 minutes for the same logged-in user.
        // If we already have the user and profile loaded, there is nothing to update —
        // returning here prevents every Dashboard module from re-rendering and
        // re-fetching its data on every silent token refresh.
        if (
          event === 'TOKEN_REFRESHED' &&
          session?.user?.id === userRef.current?.id &&
          profileRef.current
        ) {
          return;
        }

        if (session?.user) {
          setUser(session.user);
          setError(null);

          let profileData = await fetchProfile(session.user.id);
          if (!profileData) {
            await new Promise(r => setTimeout(r, 1500));
            profileData = await fetchProfile(session.user.id);
          }
          if (!profileData) {
            await new Promise(r => setTimeout(r, 3000));
            profileData = await fetchProfile(session.user.id);
          }

          setProfile(profileData);
          setProfileError(
            profileData
              ? null
              : 'Your profile could not be loaded. Please refresh the page or sign out and back in.'
          );
        } else {
          setUser(null);
          setProfile(null);
          setProfileError(null);
        }
      }
    );

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  // Recover session when the tab becomes visible again.
  // Browsers throttle setInterval in background tabs, so Supabase's
  // autoRefreshToken can miss the refresh window and the JWT expires.
  useEffect(() => {
    let lastVisibleAt = Date.now();

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;

      const elapsed = Date.now() - lastVisibleAt;
      lastVisibleAt = Date.now();

      // Only bother if tab was hidden for > 30 seconds
      if (elapsed < 30_000) return;

      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          // Transient network error — do NOT sign the user out; next visibility
          // event will retry. Treating every network blip as a sign-out was causing
          // false logouts and stuck loading screens.
          console.warn('Tab visibility: session check skipped (transient error):', sessionError.message);
          return;
        }

        if (!session) {
          // Confirmed no session — clear auth state so AppRouter redirects to /auth
          if (userRef.current) {
            setUser(null);
            setProfile(null);
          }
          return;
        }

        // If the JWT is about to expire, refresh it proactively.
        // We return immediately after because onAuthStateChange TOKEN_REFRESHED
        // will fire and the fast-path above will handle it without re-rendering.
        const expiresAt = session.expires_at ?? 0;
        const nowSeconds = Math.floor(Date.now() / 1000);
        if (expiresAt - nowSeconds < 60) {
          const { error: refreshError } = await supabase.auth.refreshSession();
          if (refreshError) {
            // Refresh token is expired — session is truly gone
            setUser(null);
            setProfile(null);
          }
          // Either TOKEN_REFRESHED fires (handled by fast-path) or we just signed
          // out above. Either way, nothing else to do here.
          return;
        }

        // Token is valid. If we somehow lost the profile (e.g., the user signed in
        // from another tab), recover it. Otherwise leave state alone — we do NOT
        // clear the cache or re-fetch every module's data on every tab focus.
        if (session.user && !profileRef.current) {
          clearCache(session.user.id);
          const profileData = await fetchProfile(session.user.id);
          if (profileData) {
            setProfile(profileData);
            setProfileError(null);
          }
        }
      } catch {
        // Network error — skip, next visibility event will retry
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchProfile]);

  const signIn = async (email: string, password: string): Promise<{ error: string | null }> => {
    try {
      setError(null);
      setProfileError(null);
      
      const { user: signedInUser, error: signInError } = await authSignIn(email, password);
      if (signInError) {
        setError(signInError);
        return { error: signInError };
      }
      
      if (signedInUser) {
        setUser(signedInUser);

        let profileData = await fetchProfile(signedInUser.id);
        if (!profileData) {
          await new Promise(r => setTimeout(r, 1500));
          profileData = await fetchProfile(signedInUser.id);
        }
        if (!profileData) {
          await new Promise(r => setTimeout(r, 3000));
          profileData = await fetchProfile(signedInUser.id);
        }

        setProfile(profileData);
        setProfileError(
          profileData
            ? null
            : 'Your profile could not be loaded. Please refresh the page or sign out and back in.'
        );
      }

      return { error: null };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign in failed';
      setError(message);
      return { error: message };
    }
  };

  const signOut = async () => {
    console.log('🚪 Signing out user');
    try {
      setUser(null);
      setProfile(null);
      setError(null);
      setProfileError(null);
      
      await supabase.auth.signOut();
    } catch (err) {
      console.error('❌ Sign out error:', err);
    }
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) {
      throw new Error('No user logged in');
    }

    console.log('🔄 Updating profile for user:', user.id, 'with updates:', updates);
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single();

      if (error) {
        console.error('❌ Profile update error:', error);
        throw new Error(error.message);
      }

      if (data) {
        console.log('✅ Profile updated successfully:', data);
        setProfile(data);
        setProfileError(null);
      }
    } catch (err) {
      console.error('❌ Profile update failed:', err);
      throw err;
    }
  };

  const refreshProfile = useCallback(async () => {
    if (!user) return;
    
    console.log('🔄 Refreshing profile for user:', user.id);
    const profileData = await fetchProfile(user.id);
    setProfile(profileData);
    setProfileError(null);
  }, [user, fetchProfile]);

  const setModuleLoadingState = useCallback((module: string, loading: boolean) => {
    setModuleLoadingStates(prev => {
      // Prevent unnecessary state updates
      if (prev[module] === loading) {
        return prev;
      }
      
      // Prevent setting loading to true if already loading
      if (loading && prev[module]) {
        return prev;
      }
      
      return {
        ...prev,
        [module]: loading
      };
    });
  }, []);

  const clearModuleLoadingState = useCallback((module: string) => {
    setModuleLoadingStates(prev => {
      // Only update if the module exists
      if (!prev[module]) {
        return prev;
      }
      
      const newState = { ...prev };
      delete newState[module];
      return newState;
    });
  }, []);

  if (process.env.NODE_ENV === 'development') {
    console.log('AuthProvider render:', { 
      loading, 
      user: user?.id, 
      profile: profile?.id, 
      error, 
      profileError,
      initialized: initializedRef.current
    });
  }

  return (
    <AuthContext.Provider value={{ 
      user, 
      profile, 
      loading, 
      error, 
      profileError, 
      retryProfileFetch, 
      refreshProfile,
      signIn, 
      signOut, 
      updateProfile,
      moduleLoadingStates,
      setModuleLoadingState,
      clearModuleLoadingState,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
} 